import { Component, Inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { OrderTransferCancellationPreview, OrderTransferCancellationService } from './order-transfer-cancellation.service';

@Component({
  selector: 'app-transfer-cancellation-dialog',
  template: `
    <h2 mat-dialog-title>Отмена перемещения</h2>
    <mat-dialog-content>
      <div class="context">
        <strong *ngIf="data.preview.movingDocId">Перемещение №{{ data.preview.movingDocId }}</strong>
        <strong *ngIf="!data.preview.movingDocId">Ожидает организации перемещения</strong>
        <span *ngIf="data.preview.sourceWarehouse">{{ data.preview.sourceWarehouse }} → {{ data.preview.targetWarehouse }}</span>
        <span>{{ data.product }} · {{ data.article }}</span>
        <b>Отменить {{ data.preview.quantity | number: '1.0-2' }} шт.</b>
      </div>
      <p>Только этот товар для заказа клиента №{{ data.preview.orderId }}. Другие товары в перемещении сохранятся.</p>
      <p *ngIf="data.preview.pickedQuantity > 0" class="returnNotice">
        Уже отобрано {{ data.preview.pickedQuantity | number: '1.0-2' }} шт.
        На складе появится задача возврата. Резерв отобранного товара освободится после её выполнения.
      </p>
      <p *ngIf="(data.preview.shelfReturnQuantity || 0) > 0" class="returnNotice">
        В ячейке заказа на складе «{{ data.preview.sourceWarehouse }}» находится
        {{ data.preview.shelfReturnQuantity | number: '1.0-2' }} шт.
        Товар останется в текущей ячейке и сразу станет свободным.
        Появится задача переместить его из ячейки заказа в свободное хранение.
      </p>
      <mat-form-field appearance="outline">
        <mat-label>Причина отмены</mat-label>
        <textarea matInput cdkFocusInitial [formControl]="reason" rows="3" maxlength="240"
          [readonly]="saving || uncertain"></textarea>
        <mat-error>Укажите причину отмены</mat-error>
      </mat-form-field>
      <p class="error" role="alert" *ngIf="error">{{ error }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button type="button" (click)="close()" [disabled]="saving">Назад</button>
      <button mat-flat-button color="warn" type="button" (click)="confirm()" [disabled]="saving || !canConfirm">
        {{ saving ? 'Отменяем…' : uncertain ? 'Проверить отмену' : 'Отменить перемещение' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .context { display: flex; flex-direction: column; gap: 6px; padding: 12px; border-radius: 8px; background: #f5f3ff; }
    mat-form-field { width: 100%; }
    p { color: #475569; line-height: 1.5; }
    .returnNotice { padding: 10px; background: #fffbeb; border-radius: 6px; }
    .error { color: #b91c1c; }
    mat-dialog-actions { gap: 8px; }
  `]
})
export class TransferCancellationDialogComponent {
  readonly reason = new UntypedFormControl('', [Validators.required, Validators.maxLength(240)]);
  saving = false;
  uncertain = false;
  error = '';
  private operationId: string;
  private sentReason = '';

  constructor(private cancellation: OrderTransferCancellationService,
    private dialogRef: MatDialogRef<TransferCancellationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { preview: OrderTransferCancellationPreview; product: string; article: string }) {
    this.operationId = cancellation.operationId();
  }

  get canConfirm() {
    return this.reason.valid && Boolean(String(this.reason.value || '').trim());
  }

  close() { if (!this.saving) this.dialogRef.close(); }

  async confirm() {
    if (this.saving || !this.canConfirm) return;
    const reason = String(this.reason.value).trim();
    if (this.sentReason && this.sentReason !== reason) this.operationId = this.cancellation.operationId();
    this.sentReason = reason;
    this.saving = true;
    this.error = '';
    this.dialogRef.disableClose = true;
    try {
      await this.cancellation.cancel(this.data.preview, reason, this.operationId).toPromise();
      this.dialogRef.close(true);
    } catch (error) {
      const status = Number((error as any)?.status || 0);
      this.uncertain = status === 0 || status >= 500;
      this.error = this.uncertain
        ? 'Не удалось получить подтверждение сервера. Нажмите «Проверить отмену» для безопасного повтора.'
        : extractApiErrorMessage((error as any)?.error || error, 'Не удалось отменить перемещение');
    } finally {
      this.saving = false;
      this.dialogRef.disableClose = false;
    }
  }
}
